import { useEffect, useRef, useState } from "react";
import { CapDoc } from "../capTypes";
import {
  clearCapRepRedesignDocRefreshState,
  getCapRepRedesignBaselineDocId,
  shouldPollForRepRedesignCapDocs,
} from "../utils/capRepRedesignDocRefresh";
import {
  CAP_DOCS_MAY_STILL_BE_UPDATING_MESSAGE,
  MAX_POLL_ATTEMPTS,
  POLL_RETRY_DELAY_MS,
  pollForUpdatedCapPackageDoc,
} from "./capRepRedesignDocPollingLogic";

type UseCapRepRedesignDocPollingArgs = {
  prospectId?: string;
  refetchCapDocuments: () => Promise<CapDoc[] | undefined>;
  enabled: boolean;
};

const useCapRepRedesignDocPolling = ({
  prospectId,
  refetchCapDocuments,
  enabled,
}: UseCapRepRedesignDocPollingArgs) => {
  const [isPollingForNewCapDocs, setIsPollingForNewCapDocs] = useState(false);
  const [capDocsMayStillBeUpdating, setCapDocsMayStillBeUpdating] =
    useState(false);
  const [returnedFromRepRedesign, setReturnedFromRepRedesign] = useState(false);

  const refetchRef = useRef(refetchCapDocuments);
  refetchRef.current = refetchCapDocuments;

  useEffect(() => {
    if (
      !enabled ||
      !prospectId ||
      !shouldPollForRepRedesignCapDocs(prospectId)
    ) {
      return;
    }

    const baselineDocId = getCapRepRedesignBaselineDocId(prospectId);
    if (!baselineDocId) {
      return;
    }

    setReturnedFromRepRedesign(true);

    let cancelled = false;

    const pollForNewCapDocs = async () => {
      setIsPollingForNewCapDocs(true);
      setCapDocsMayStillBeUpdating(false);

      const foundNewDocs = await pollForUpdatedCapPackageDoc({
        baselineDocId,
        refetchCapDocuments: () => refetchRef.current(),
        shouldContinue: () => !cancelled,
      });

      if (cancelled) {
        return;
      }

      clearCapRepRedesignDocRefreshState(prospectId);

      if (!foundNewDocs) {
        setCapDocsMayStillBeUpdating(true);
      }

      setIsPollingForNewCapDocs(false);
    };

    void pollForNewCapDocs();

    return () => {
      cancelled = true;
    };
  }, [enabled, prospectId]);

  return {
    capDocsMayStillBeUpdating,
    isPollingForNewCapDocs,
    returnedFromRepRedesign,
  };
};

export {
  CAP_DOCS_MAY_STILL_BE_UPDATING_MESSAGE,
  MAX_POLL_ATTEMPTS,
  POLL_RETRY_DELAY_MS,
  useCapRepRedesignDocPolling,
};
